import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Cloud, CloudFog, CloudLightning, CloudRain, CloudSnow, Loader2, Sun, CloudSun, Wind, Droplets } from 'lucide-react';

// Maps WMO codes to Icons and labels
const getWeatherMeta = (code) => {
  if (code === undefined || code === null) return { icon: Cloud, label: 'Onbekend' };
  if (code === 0) return { icon: Sun, label: 'Zonnig' };
  if (code >= 1 && code <= 2) return { icon: CloudSun, label: 'Licht bewolkt' };
  if (code === 3) return { icon: Cloud, label: 'Bewolkt' };
  if (code === 45 || code === 48) return { icon: CloudFog, label: 'Mist' };
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return { icon: CloudRain, label: 'Regen' };
  if (code >= 71 && code <= 77) return { icon: CloudSnow, label: 'Sneeuw' };
  if (code >= 95 && code <= 99) return { icon: CloudLightning, label: 'Onweer' };
  return { icon: Cloud, label: 'Wisselvallig' };
};

export function WeatherWidget({ locationSlug }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    // Get today and next 2 days
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 3; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      dates.push(d.toISOString().split('T')[0]);
    }
    const datesQuery = dates.join(',');

    api.get('/weather/forecast', { params: { location_id: locationSlug, dates: datesQuery } })
      .then(res => {
        if (!cancelled) {
          const forecast = res.data.forecast || {};
          // Ensure we have at least today's data
          if (Object.keys(forecast).length === 0) {
            setError(true);
          } else {
            setData({ dates, forecast });
          }
          setLoading(false);
        }
      })
      .catch(err => {
        console.error('Weather load failed', err);
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [locationSlug]);

  if (loading) {
    return (
      <div className="rounded-lg border border-[#e4e9f0] bg-white p-5 shadow-sm border-l-[3px] border-l-tw1-brand-navy flex items-center gap-3">
        <Loader2 size={16} className="animate-spin text-tw1-text-muted" />
        <span className="text-sm text-tw1-text-muted">Weer ophalen…</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg border border-[#e4e9f0] bg-white p-5 shadow-sm border-l-[3px] border-l-slate-300 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-500">Lokale Weersverwachting</h3>
          <p className="text-xs text-slate-400 mt-0.5">Weer niet beschikbaar voor deze locatie.</p>
        </div>
        <div className="flex h-[38px] w-[38px] items-center justify-center rounded-lg bg-slate-100 text-slate-400">
          <Cloud size={18} />
        </div>
      </div>
    );
  }

  const { dates, forecast } = data;
  const todayStr = dates[0];
  const todayForecast = forecast[todayStr];
  
  if (!todayForecast) {
    // Edge case where api succeeds but today's forecast is missing
    return (
      <div className="rounded-lg border border-[#e4e9f0] bg-white p-5 shadow-sm border-l-[3px] border-l-slate-300 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-500">Lokale Weersverwachting</h3>
          <p className="text-xs text-slate-400 mt-0.5">Actueel weer niet beschikbaar.</p>
        </div>
        <div className="flex h-[38px] w-[38px] items-center justify-center rounded-lg bg-slate-100 text-slate-400">
          <Cloud size={18} />
        </div>
      </div>
    );
  }

  const todayMeta = getWeatherMeta(todayForecast.weather_code);
  const TodayIcon = todayMeta.icon;

  const nextDays = dates.slice(1).map(d => ({
    dateStr: d,
    dateObj: new Date(d),
    data: forecast[d]
  })).filter(d => d.data); // only include if data exists

  return (
    <div className="relative overflow-hidden rounded-lg border border-[#e4e9f0] bg-white p-5 shadow-sm border-l-[3px] border-l-tw1-brand-navy">
      <h3 className="text-sm font-semibold text-tw1-brand-navy mb-4">Lokale Weersverwachting</h3>
      
      <div className="flex flex-wrap items-center gap-6">
        {/* Today Main */}
        <div className="flex items-center gap-4 min-w-[200px]">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#eef1f7] text-tw1-brand-navy">
            <TodayIcon size={24} />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-tw1-text-muted mb-0.5">Vandaag</div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums text-tw1-brand-navy">
                {Math.round(todayForecast.temperature_max)}°
              </span>
              <span className="text-sm font-medium text-tw1-text-muted">{todayMeta.label}</span>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-tw1-text-muted-light">
              <span className="flex items-center gap-1" title="Neerslag">
                <Droplets size={12} /> {todayForecast.precipitation_mm}mm
              </span>
              <span className="flex items-center gap-1" title="Max wind">
                <Wind size={12} /> {Math.round(todayForecast.wind_speed_kmh)}km/h
              </span>
            </div>
          </div>
        </div>

        {/* Vertical Divider */}
        {nextDays.length > 0 && (
          <div className="hidden sm:block h-12 w-px bg-[#e4e9f0]" />
        )}

        {/* Forecast */}
        {nextDays.length > 0 && (
          <div className="flex items-center gap-5">
            {nextDays.map(({ dateStr, dateObj, data: d }) => {
              const meta = getWeatherMeta(d.weather_code);
              const DayIcon = meta.icon;
              return (
                <div key={dateStr} className="text-center">
                  <div className="text-[11px] font-semibold text-tw1-text-muted uppercase mb-1">
                    {dateObj.toLocaleDateString('nl-NL', { weekday: 'short' })}
                  </div>
                  <div className="flex justify-center mb-1 text-tw1-brand-navy/70">
                    <DayIcon size={16} />
                  </div>
                  <div className="text-sm font-bold tabular-nums text-tw1-brand-navy">
                    {Math.round(d.temperature_max)}°
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
